/**
 * useAssessmentForm Hook
 * Manages assessment form state and logic
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useDebounce, useConceptValues } from "../../hooks";
import { apiClient } from "../../services/apiClient";
import { submitBundleTask } from "../../services/assessmentService";
import type {
  Concept,
  AssessmentConcept,
  AssessmentRegulation,
  AssessmentConceptValue,
} from "../../types";

interface UseAssessmentFormProps {
  selectedRequirementIds: string[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export function useAssessmentForm({
  selectedRequirementIds,
  onSuccess,
  onError,
}: UseAssessmentFormProps) {
  const parseKeywords = (raw: string): string[] =>
    raw
      .trim()
      .split(/,+/)
      .map((word) => word.trim())
      .filter((word) => word.length > 0);

  const getSubmissionValidationError = (params: {
    hasAtLeastOneValue: boolean;
    totalDropdownSelections: number;
    parsedKeywordsCount: number;
  }): string | null => {
    if (!params.hasAtLeastOneValue) {
      return "Please choose at least one option or enter keywords.";
    }
    if (params.totalDropdownSelections > 20) {
      return "Total selections across all categories cannot exceed 20.";
    }
    if (params.parsedKeywordsCount > 5) {
      return "Maximum 5 keywords allowed.";
    }
    return null;
  };

  // Form state
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>(
    []
  );
  const [selectedIngredients, setSelectedIngredients] = useState<string[]>([]);
  const [selectedTherapeuticAreas, setSelectedTherapeuticAreas] = useState<
    string[]
  >([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [keywords, setKeywords] = useState("");

  // Concepts data
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [isLoadingConcepts, setIsLoadingConcepts] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // AbortController for canceling requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Use concept values hooks
  const productTypesHook = useConceptValues("ProductType", false);
  const ingredientsHook = useConceptValues("IngredientName", false);
  const therapeuticAreasHook = useConceptValues("TherapeuticArea", false);
  const departmentsHook = useConceptValues("Department", false);

  // Store hook references to avoid dependency issues in loadConcepts
  const hooksRef = useRef({
    productTypesHook,
    ingredientsHook,
    therapeuticAreasHook,
    departmentsHook,
  });

  // Update refs when hooks change
  useEffect(() => {
    hooksRef.current = {
      productTypesHook,
      ingredientsHook,
      therapeuticAreasHook,
      departmentsHook,
    };
  }, [
    productTypesHook,
    ingredientsHook,
    therapeuticAreasHook,
    departmentsHook,
  ]);

  // Debounced search handlers
  const handleProductTypeSearch = useDebounce<[string]>(
    (searchText: string) => {
      console.log("[ProductType Search] Triggered with text:", searchText);
      productTypesHook.search(searchText);
    },
    500
  );

  const handleIngredientSearch = useDebounce<[string]>((searchText: string) => {
    console.log("[Ingredient Search] Triggered with text:", searchText);
    ingredientsHook.search(searchText);
  }, 500);

  const handleTherapeuticAreaSearch = useDebounce<[string]>(
    (searchText: string) => {
      console.log("[TherapeuticArea Search] Triggered with text:", searchText);
      therapeuticAreasHook.search(searchText);
    },
    500
  );

  const handleDepartmentSearch = useDebounce<[string]>((searchText: string) => {
    console.log("[Department Search] Triggered with text:", searchText);
    departmentsHook.search(searchText);
  }, 500);

  // Load concepts
  const loadConcepts = useCallback(async () => {
    console.log("📥 [useAssessmentForm] loadConcepts called - START");
    setIsLoadingConcepts(true);
    try {
      console.log("🌐 [useAssessmentForm] Fetching concepts from API...");
      const result = await apiClient.get<Concept[]>(
        "/knowledge_manager/concepts"
      );
      console.log("[useAssessmentForm] Concepts data:", result);

      if (result.status === "success" && result.data) {
        setConcepts(result.data);

        // Trigger initial loads using refs to avoid dependency issues
        console.log(
          "🔄 [useAssessmentForm] Triggering hook reloads (4 API calls)..."
        );
        const hooks = hooksRef.current;
        hooks.productTypesHook.reload();
        hooks.ingredientsHook.reload();
        hooks.therapeuticAreasHook.reload();
        hooks.departmentsHook.reload();
        console.log("✅ [useAssessmentForm] Hook reloads triggered");
      }
    } catch (error) {
      console.error("[useAssessmentForm] Error fetching concepts:", error);
    } finally {
      setIsLoadingConcepts(false);
      console.log("📥 [useAssessmentForm] loadConcepts called - END");
    }
  }, []); // No dependencies - use refs instead

  // Reset form
  const resetForm = useCallback(() => {
    setSelectedProductTypes([]);
    setSelectedIngredients([]);
    setSelectedTherapeuticAreas([]);
    setSelectedDepartments([]);
    setKeywords("");
  }, []);

  // Abort submission
  const abortSubmission = useCallback(() => {
    if (abortControllerRef.current) {
      console.log("🚫 [useAssessmentForm] Aborting submission...");
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Validation
  const totalDropdownSelections =
    selectedProductTypes.length +
    selectedIngredients.length +
    selectedTherapeuticAreas.length +
    selectedDepartments.length;

  const keywordsList = keywords
    .trim()
    .split(/,+/)
    .map((word) => word.trim())
    .filter((word) => word.length > 0);

  const hasAtLeastOneValue =
    selectedProductTypes.length > 0 ||
    selectedIngredients.length > 0 ||
    selectedTherapeuticAreas.length > 0 ||
    selectedDepartments.length > 0 ||
    keywordsList.length > 0;

  const isValidSelection =
    totalDropdownSelections <= 20 && keywordsList.length <= 5;

  // Submit assessment
  const submitAssessment = useCallback(async () => {
    const parsedKeywords = parseKeywords(keywords);

    const validationError = getSubmissionValidationError({
      hasAtLeastOneValue,
      totalDropdownSelections,
      parsedKeywordsCount: parsedKeywords.length,
    });
    if (validationError) {
      onError(validationError);
      return false;
    }

    setIsSubmitting(true);

    // Create AbortController for this submission
    abortControllerRef.current = new AbortController();

    try {
      // Fetch complete regulation data
      const regulationPromises = selectedRequirementIds.map(async (id) => {
        try {
          const result = await apiClient.get(`/regulations/${id}`);
          if (result.status === "success" && result.data) {
            return {
              id: result.data.regulation_id || result.data.id || id,
              version: result.data.version ?? "1.0",
              title: result.data.title || `Regulation ${id}`,
              documents: result.data.associated_documents || [],
            };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching regulation ${id}:`, error);
          return null;
        }
      });

      const regulationResults = await Promise.all(regulationPromises);
      const regulations: AssessmentRegulation[] = regulationResults.filter(
        (reg): reg is AssessmentRegulation => reg !== null
      );

      if (regulations.length === 0) {
        onError("Failed to fetch regulation details. Please try again.");
        return false;
      }

      // Build concepts array
      const assessmentConcepts: AssessmentConcept[] = [];

      const buildConcept = async (
        conceptName: string,
        conceptCode: string,
        conceptDesc: string,
        conceptApiName: string,
        selectedValues: string[]
      ) => {
        if (selectedValues.length === 0) {
          return null;
        }

        // Build values array by fetching each selected value individually
        // This approach works better with backend pagination - instead of fetching all values,
        // we only fetch the specific values that were selected
        const values: AssessmentConceptValue[] = [];

        for (const selectedValue of selectedValues) {
          try {
            // Fetch this specific value with name parameter for exact match
            const result = await apiClient.get<AssessmentConceptValue[]>(
              "/knowledge_manager/concepts/values",
              {
                concept_name: conceptApiName,
                name: selectedValue,
              }
            );

            if (result.status === "success" && result.data && result.data.length > 0) {
              const valueDetail = result.data[0];
              const payloadItem = {
                name: valueDetail.name,
                code: valueDetail.code ?? valueDetail.name,
                description: valueDetail.description ?? valueDetail.name,
              };
              
              console.log(`[bundle-task] Fetched value "${selectedValue}":`, payloadItem);
              values.push(payloadItem);
            } else {
              // Fallback if API doesn't return data
              console.warn(`[bundle-task] Value "${selectedValue}" not found in API, using fallback`);
              values.push({
                name: selectedValue,
                code: selectedValue,
                description: selectedValue,
              });
            }
          } catch (error) {
            console.error(`[bundle-task] Error fetching value "${selectedValue}":`, error);
            // Use fallback on error
            values.push({
              name: selectedValue,
              code: selectedValue,
              description: selectedValue,
            });
          }
        }

        if (values.length === 0) {
          return null;
        }

        // Use conceptCode (GCC code) in the final payload
        return {
          concept: {
            name: conceptName,
            code: conceptCode,
            description: conceptDesc,
          },
          values,
        };
      };

      // Build concepts for each category
      // Find the concept data from the concepts list to get the correct GCC code
      const productTypeConceptData = concepts.find(
        (c) => c.name === "ProductType"
      );
      const productTypeConcept = await buildConcept(
        "Product Type",
        productTypeConceptData?.code ?? "GCC-C000060",
        productTypeConceptData?.description ?? "Type of medical product",
        "ProductType", // API name for fetching values
        selectedProductTypes
      );
      if (productTypeConcept) assessmentConcepts.push(productTypeConcept);

      const ingredientConceptData = concepts.find(
        (c) => c.name === "IngredientName"
      );
      const ingredientConcept = await buildConcept(
        "Ingredient",
        ingredientConceptData?.code ?? "GCC-C000033",
        ingredientConceptData?.description ?? "Active ingredient",
        "IngredientName", // API name for fetching values
        selectedIngredients
      );
      if (ingredientConcept) assessmentConcepts.push(ingredientConcept);

      const therapeuticAreaConceptData = concepts.find(
        (c) => c.name === "TherapeuticArea"
      );
      const therapeuticAreaConcept = await buildConcept(
        "Therapeutic Area",
        therapeuticAreaConceptData?.code ?? "GCC-C000085",
        therapeuticAreaConceptData?.description ?? "Medical therapeutic area",
        "TherapeuticArea", // API name for fetching values
        selectedTherapeuticAreas
      );
      if (therapeuticAreaConcept)
        assessmentConcepts.push(therapeuticAreaConcept);

      const departmentConceptData = concepts.find((c) => c.code === "dp");
      const departmentConcept = await buildConcept(
        "Department",
        departmentConceptData?.code ?? "dp",
        departmentConceptData?.description ?? "Department",
        departmentConceptData?.name ?? "Department", // API name for fetching values
        selectedDepartments
      );
      if (departmentConcept) assessmentConcepts.push(departmentConcept);

      // Submit assessment
      const payload = {
        regulations,
        concepts: assessmentConcepts,
        keywords: parsedKeywords,
      };

      const response = await submitBundleTask(payload);

      if (response.status === "success" || response.code === 200) {
        onSuccess(
          `Successfully submitted assessment for ${selectedRequirementIds.length} regulation(s).`
        );
        resetForm();
        return true;
      } else {
        throw new Error(response.message ?? "Assessment submission failed");
      }
    } catch (error) {
      // Check if the error is due to abort
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Assessment submission was cancelled by user");
        return false;
      }
      
      console.error("Assessment error:", error);
      onError(
        error instanceof Error
          ? error.message
          : "Failed to assess impact. Please try again."
      );
      return false;
    } finally {
      setIsSubmitting(false);
      abortControllerRef.current = null;
    }
  }, [
    selectedRequirementIds,
    selectedProductTypes,
    selectedIngredients,
    selectedTherapeuticAreas,
    selectedDepartments,
    keywords,
    concepts,
    hasAtLeastOneValue,
    totalDropdownSelections,
    onSuccess,
    onError,
    resetForm,
  ]);

  return {
    // Form state
    selectedProductTypes,
    setSelectedProductTypes,
    selectedIngredients,
    setSelectedIngredients,
    selectedTherapeuticAreas,
    setSelectedTherapeuticAreas,
    selectedDepartments,
    setSelectedDepartments,
    keywords,
    setKeywords,

    // Concept values
    productTypes: productTypesHook.values,
    loadingProductTypes: productTypesHook.loading,
    ingredients: ingredientsHook.values,
    loadingIngredients: ingredientsHook.loading,
    therapeuticAreas: therapeuticAreasHook.values,
    loadingTherapeuticAreas: therapeuticAreasHook.loading,
    departments: departmentsHook.values,
    loadingDepartments: departmentsHook.loading,

    // Search handlers
    handleProductTypeSearch,
    handleIngredientSearch,
    handleTherapeuticAreaSearch,
    handleDepartmentSearch,

    // State
    isLoadingConcepts,
    isSubmitting,
    concepts,

    // Validation
    hasAtLeastOneValue,
    isValidSelection,
    totalDropdownSelections,
    keywordsList,

    // Actions
    loadConcepts,
    resetForm,
    submitAssessment,
    abortSubmission,
  };
}
